import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { fakePlay, fakePurchase } from '@app/shared/models/testerModal.model';
import { environment } from '@environments/environment';
import { lastValueFrom } from 'rxjs';
import { TesterModalFacade } from './tester-modal.facade';
import { FrontendVersionResponse } from '@app/shared/models/version.model';
import { LoggerService } from '@app/shared/services/logger.service';
import { ToastrUtils } from '@app/shared/utils/toastrs';
import { GamesRepository } from '@app/shared/state/games/games.repository';

@Injectable({
  providedIn: 'root',
})
export class TesterModalService {
  private readonly http = inject(HttpClient);
  private readonly testerModalFacade = inject(TesterModalFacade);
  private readonly logger = inject(LoggerService);
  private readonly toastrUtils = inject(ToastrUtils);
  private readonly gamesRepo = inject(GamesRepository);

  private readonly testerServiceUrl = `${environment.apiUrl}/api/test`;
  private httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  constructor() {
    if (!environment.production) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).modalTester = this;
    }
  }

  async addPrestige(points: number): Promise<void> {
    try {
      await lastValueFrom(this.http.post<void>(`${this.testerServiceUrl}/add-prestige`, points));
      this.toastrUtils.throwSimpleToastr('Success', 'Prestige added successfully');
    } catch (error) {
      this.logger.error('Error on add prestige', error);
      this.toastrUtils.throwSimpleToastr('Error', 'Error on add prestige');
    }
  }

  async addCredits(credits: number): Promise<void> {
    try {
      await lastValueFrom(this.http.post<void>(`${this.testerServiceUrl}/add-credits`, credits));
      this.toastrUtils.throwSimpleToastr('Success', 'Credits added successfully');
    } catch (error) {
      this.logger.error('Error on add credits', error);
      this.toastrUtils.throwSimpleToastr('Error', 'Error on add credits');
    }
  }

  async addGems(gems: number): Promise<void> {
    try {
      await lastValueFrom(this.http.post<void>(`${this.testerServiceUrl}/add-gems`, gems));
      this.toastrUtils.throwSimpleToastr('Success', 'Gems added successfully');
    } catch (error) {
      this.logger.error('Error on add gems', error);
      this.toastrUtils.throwSimpleToastr('Error', 'Error on add gems');
    }
  }

  async unlockGame(gameId: string): Promise<void> {
    try {
      await lastValueFrom(
        this.http.post<void>(`${this.testerServiceUrl}/unlock-game`, JSON.stringify(gameId), this.httpOptions)
      );
      const game = this.gamesRepo.getGameById(gameId);
      this.toastrUtils.throwUnlockGameToastr(game);
    } catch (error) {
      this.logger.error('Error on unlock game', error);
      this.toastrUtils.throwSimpleToastr('Error', 'Error on unlock game');
    }
  }

  async setTrophyOnePointToComplete(gameId: string): Promise<void> {
    try {
      await lastValueFrom(
        this.http.put<void>(
          `${this.testerServiceUrl}/set-trophy-one-point-to-complete`,
          JSON.stringify(gameId),
          this.httpOptions
        )
      );
      this.toastrUtils.throwSimpleToastr('Success', `${gameId} Trophy One Point to complete`);
    } catch (error) {
      this.logger.error('Error on set trophy one point to complete', error);
      this.toastrUtils.throwSimpleToastr('Error', 'Error on set trophy one point to complete');
    }
  }

  async resetGoldenBomb() {
    try {
      await lastValueFrom(this.http.put<void>(`${this.testerServiceUrl}/reset-golden-bomb`, {}, this.httpOptions));
      this.testerModalFacade.loadGoldenBomb();
      this.toastrUtils.throwSimpleToastr('Success', 'Golden Bomb reset successfully');
    } catch (error) {
      this.logger.error('Error on reset golden bomb', error);
      this.toastrUtils.throwSimpleToastr('Error', 'Error on reset golden bomb');
    }
  }

  async resetRecurring() {
    try {
      await lastValueFrom(this.http.put<void>(`${this.testerServiceUrl}/reset-recurring`, {}, this.httpOptions));
      this.toastrUtils.throwSimpleToastr('Success', 'Recurring reset successfully');
    } catch (error) {
      this.logger.error('Error on reset recurring', error);
      this.toastrUtils.throwSimpleToastr('Error', 'Error on reset recurring');
    }
  }

  async resetHighScore() {
    try {
      await lastValueFrom(this.http.put<void>(`${this.testerServiceUrl}/reset-highscore`, {}, this.httpOptions));
      this.testerModalFacade.reloadPage();
    } catch (error) {
      this.logger.error('Error on reset high score', error);
    }
  }

  async setLastLoginYesterday() {
    try {
      await lastValueFrom(
        this.http.put<void>(`${this.testerServiceUrl}/set-last-login-yesterday`, {}, this.httpOptions)
      );
      this.testerModalFacade.reloadPage();
    } catch (error) {
      this.logger.error('Error on set last login yesterday', error);
    }
  }

  async setChallengesOnePointToComplete(eventId: string) {
    try {
      await lastValueFrom(
        this.http.put<void>(
          `${this.testerServiceUrl}/set-challenges-one-point-to-complete`,
          JSON.stringify(eventId),
          this.httpOptions
        )
      );
      this.toastrUtils.throwSimpleToastr('Success', 'Set challenge one point to complete successfully');
    } catch (error) {
      this.logger.error('Error on set challenge one point to complete', error);
      this.toastrUtils.throwSimpleToastr('Error', 'Error on set challenge one point to complete');
    }
  }

  async deleteAllUserLiveopStatuses() {
    try {
      await lastValueFrom(
        this.http.delete<void>(`${this.testerServiceUrl}/delete-all-user-liveop-statuses`, this.httpOptions)
      );
      this.testerModalFacade.reloadPage();
    } catch (error) {
      this.logger.error('Error on delete all user liveop statuses', error);
    }
  }

  async fakePurchase(purchase: fakePurchase) {
    try {
      await lastValueFrom(this.http.post<void>(`${this.testerServiceUrl}/fake-purchase`, purchase));
      this.toastrUtils.throwSimpleToastr('Success', 'Fake purchase successful');
    } catch (error) {
      this.logger.error('Error on fake purchase', error);
      this.toastrUtils.throwSimpleToastr('Error', 'Error on fake purchase');
    }
  }

  async fakePlay(play: fakePlay) {
    try {
      await lastValueFrom(this.http.post<void>(`${this.testerServiceUrl}/fake-play`, play));
      this.toastrUtils.throwSimpleToastr('Success', 'Fake play successful');
    } catch (error) {
      this.logger.error('Error on fake play', error);
      this.toastrUtils.throwSimpleToastr('Error', 'Error on fake play');
    }
  }

  async updateEmail(email: string) {
    try {
      await lastValueFrom(this.http.put<void>(`${this.testerServiceUrl}/update-email/${email}`, {}, this.httpOptions));
      this.testerModalFacade.reloadPage();
    } catch (error) {
      this.logger.error('Error on update email', error);
      this.toastrUtils.throwSimpleToastr('Error', 'Error on update email');
    }
  }

  async deleteEmailLogin() {
    try {
      await lastValueFrom(this.http.delete<void>(`${this.testerServiceUrl}/delete-email-login`, this.httpOptions));
      this.testerModalFacade.reloadPage();
    } catch (error) {
      this.logger.error('Error on delete email login', error);
      this.toastrUtils.throwSimpleToastr('Error', 'Error on delete email login');
    }
  }

  async deleteGuestLogin() {
    try {
      await lastValueFrom(this.http.delete<void>(`${this.testerServiceUrl}/delete-guest-login`, this.httpOptions));
      this.testerModalFacade.reloadPage();
    } catch (error) {
      this.logger.error('Error on delete guest login', error);
      this.toastrUtils.throwSimpleToastr('Error', 'Error on delete guest login');
    }
  }

  async deleteFacebookLogin() {
    try {
      await lastValueFrom(this.http.delete<void>(`${this.testerServiceUrl}/delete-facebook-login`, this.httpOptions));
      this.testerModalFacade.reloadPage();
    } catch (error) {
      this.logger.error('Error on delete facebook login', error);
      this.toastrUtils.throwSimpleToastr('Error', 'Error on delete facebook login');
    }
  }

  async deleteAppleLogin() {
    try {
      await lastValueFrom(this.http.delete<void>(`${this.testerServiceUrl}/delete-apple-login`, this.httpOptions));
      this.testerModalFacade.reloadPage();
    } catch (error) {
      this.logger.error('Error on delete apple login', error);
      this.toastrUtils.throwSimpleToastr('Error', 'Error on delete apple login');
    }
  }

  async deleteGooglePlayLogin() {
    try {
      await lastValueFrom(
        this.http.delete<void>(`${this.testerServiceUrl}/delete-google-play-login`, this.httpOptions)
      );
      this.testerModalFacade.reloadPage();
    } catch (error) {
      this.logger.error('Error on delete google play login', error);
      this.toastrUtils.throwSimpleToastr('Error', 'Error on delete google play login');
    }
  }

  async getFEVersions(): Promise<string[]> {
    try {
      const versionResponse = await lastValueFrom(
        this.http.get<FrontendVersionResponse>(
          `${this.testerServiceUrl}/get-frontend-version?nameBlobContainer=cdn&nameDirectoryReference=portal`
        )
      );
      return versionResponse.availableVersions;
    } catch (error) {
      this.logger.error('Error on get FE versions', error);
      this.toastrUtils.throwSimpleToastr('Error', 'Error on get FE versions');
      return [];
    }
  }

  async setPurchaseBlocked(blocked: boolean): Promise<void> {
    try {
      await lastValueFrom(
        this.http.put<void>(`${this.testerServiceUrl}/set-purchase-blocked`, blocked, this.httpOptions)
      );
      this.toastrUtils.throwSimpleToastr('Success', 'Set Purchase status successfully');
    } catch (error) {
      this.logger.error('Error on set purchase status', error);
      this.toastrUtils.throwSimpleToastr('Error', 'Error on set purchase status');
    }
  }

  async resetAll() {
    try {
      await lastValueFrom(this.http.put<void>(`${this.testerServiceUrl}/reset-golden-bomb`, {}, this.httpOptions));
      await lastValueFrom(this.http.put<void>(`${this.testerServiceUrl}/reset-recurring`, {}, this.httpOptions));
      await lastValueFrom(this.http.put<void>(`${this.testerServiceUrl}/reset-highscore`, {}, this.httpOptions));
      await lastValueFrom(
        this.http.put<void>(`${this.testerServiceUrl}/set-last-login-yesterday`, {}, this.httpOptions)
      );
      await lastValueFrom(this.http.delete<void>(`${this.testerServiceUrl}/delete-all-user-liveop-statuses`, {}));
      this.testerModalFacade.reloadPage();
    } catch (error) {
      this.logger.error('Error on reset all', error);
      this.toastrUtils.throwSimpleToastr('Error', 'Error on reset all');
    }
  }

  async unlockAllGames() {
    try {
      await lastValueFrom(this.http.post<void>(`${this.testerServiceUrl}/add-all-games`, {}, this.httpOptions));
      this.toastrUtils.throwSimpleToastr('Success', 'Unlocked all games successfully');
      this.testerModalFacade.reloadPage();
    } catch (error) {
      this.logger.error('Error on unlock all games', error);
      this.toastrUtils.throwSimpleToastr('Error', 'Error on unlock all games');
    }
  }

  async fixUserTrophies() {
    try {
      await lastValueFrom(this.http.put<void>(`${this.testerServiceUrl}/fix-trophies`, this.httpOptions));
      this.toastrUtils.throwSimpleToastr('Success', `Fixed Trophies Successfully`);
      this.testerModalFacade.reloadPage();
    } catch (error) {
      this.logger.error('Error on fix trophies', error);
      this.toastrUtils.throwSimpleToastr('Error', 'Error on fix trophies');
    }
  }

  async createEventFromId(eventId: string): Promise<void> {
    try {
      await lastValueFrom(
        this.http.post<void>(`${this.testerServiceUrl}/create-user-event`, JSON.stringify(eventId), this.httpOptions)
      );
      this.toastrUtils.throwSimpleToastr('Success', `Created event user successfully`);
      this.testerModalFacade.reloadPage();
    } catch (error) {
      this.logger.error('Error creating events', error);
      this.toastrUtils.throwSimpleToastr('Error', 'Error creating events');
    }
  }
}
